import type {
  TConversation,
  TGetUserConversationsRequestPayload,
} from '@cascade/api-client/conversations';
import type {
  TErrorPayload,
  TToolCalledPayload,
} from '@cascade/api-client/events';
import type { TSseEvent } from '@cascade/core/connection';
import { isKeyOf, isRecord } from '@cascade/core/utils';
import { createQueryKey } from '#providers/query/index.ts';
import type { TQueryKeyPrefix } from '#providers/query/index.ts';
import type {
  TConversationSort,
  TConversationsFilter,
  TMessage,
  TMessageWithSender,
  TServerAction,
  TStreamEvent,
  TToolCall,
} from './Conversations.types.ts';

const SENSITIVE_INFORMATION_REASON = 'pii_detected';
const USER_SENDER = 'user';

export const conversationsQueryPrefix = [
  'conversations',
  'getUserConversations',
] as const satisfies TQueryKeyPrefix<'conversations'>;

const streamEvents: Record<string, TStreamEvent> = {
  chunk: { type: 'chat.answer.token' },
  message: { type: 'chat.answer.done' },
  tool_called: { type: 'chat.tool.called' },
  documents: {
    type: 'chat.documents',
    wrap: (documents) => ({ documents }),
  },
  follow_up_questions: {
    type: 'chat.follow_up_questions',
    wrap: (questions) => ({ questions }),
  },
  detected_experience: { type: 'chat.experience.detected' },
  file_delivered: { type: 'chat.file.delivered' },
};

export const isSensitiveInformationError = (error?: TErrorPayload): boolean =>
  error?.reason === SENSITIVE_INFORMATION_REASON;

export const toServerAction = (
  requestId: string,
  { event, data }: TSseEvent,
): TServerAction | undefined => {
  if (!isKeyOf(streamEvents, event)) {
    return undefined;
  }

  const streamEvent = streamEvents[event];
  const decoded: unknown = JSON.parse(data);
  const { type, wrap } = streamEvent;
  const payload = wrap ? wrap(decoded) : decoded;

  return {
    type,
    envelope: { type, request_id: requestId, payload },
  } as TServerAction;
};

export const isUserMessage = (message: TMessageWithSender): boolean =>
  message.sender === USER_SENDER;

export const isMessageUnfinished = (message: TMessage): boolean =>
  message.status === 'pending' || message.status === 'streaming';

export const getConversationsQueryKey = (
  request: TGetUserConversationsRequestPayload,
) => createQueryKey('conversations', 'getUserConversations', request);

export const isExcludingEscalated = (queryKey: readonly unknown[]): boolean => {
  const [, , request] = queryKey;

  return isRecord(request) && request.exclude_escalated === true;
};

export const markConversationEscalated = (
  conversations: TConversation[],
  conversationId: string,
  isEscalatedExcluded: boolean,
): TConversation[] => {
  if (isEscalatedExcluded) {
    return conversations.filter(
      (conversation) => conversation.conversation_id !== conversationId,
    );
  }

  return conversations.map((conversation) =>
    conversation.conversation_id === conversationId
      ? { ...conversation, is_escalated: true }
      : conversation,
  );
};

export const getMessagesQueryKey = (conversationId?: string) =>
  createQueryKey('conversations', 'getConversationMessages', {
    conversation_id: conversationId ?? '',
  });

export const getFollowUpQuestions = (messages: TMessage[]): string[] =>
  messages.at(-1)?.followUpQuestions ?? [];

const matchesTitleSearch = (
  conversation: TConversation,
  search = '',
): boolean =>
  conversation.title.toLowerCase().includes(search.trim().toLowerCase());

const isStartedBetween = (
  conversation: TConversation,
  startedFrom?: Date,
  startedUntil?: Date,
): boolean => {
  const startedAt = Date.parse(conversation.date);

  if (startedFrom && startedAt < startedFrom.getTime()) {
    return false;
  }

  return !startedUntil || startedAt <= startedUntil.getTime();
};

const compareConversations = (
  first: TConversation,
  second: TConversation,
  { field, direction }: TConversationSort,
): number => {
  const sign = direction === 'asc' ? 1 : -1;

  if (field === 'date') {
    return sign * (Date.parse(first.date) - Date.parse(second.date));
  }

  return sign * first[field].localeCompare(second[field]);
};

export const filterConversations = (
  conversations: TConversation[],
  { search, startedFrom, startedUntil }: TConversationsFilter,
): TConversation[] =>
  conversations.filter(
    (conversation) =>
      matchesTitleSearch(conversation, search) &&
      isStartedBetween(conversation, startedFrom, startedUntil),
  );

export const sortConversations = (
  conversations: TConversation[],
  sorts: TConversationSort[],
): TConversation[] =>
  [...conversations].sort((first, second) => {
    for (const sort of sorts) {
      const order = compareConversations(first, second, sort);

      if (order !== 0) {
        return order;
      }
    }

    return 0;
  });

export const addToolCall = (
  toolCalls: TToolCall[] = [],
  payload: TToolCalledPayload,
): TToolCall[] => {
  const toolCall = { ...payload, status: payload.status ?? 'running' };
  const callIndex = toolCalls.findIndex(
    (recorded) => recorded.call_id === toolCall.call_id,
  );

  if (callIndex === -1) {
    return [...toolCalls, toolCall];
  }

  return toolCalls.map((recorded, index) =>
    index === callIndex ? toolCall : recorded,
  );
};

export const keepPersistedToolCalls = (
  toolCalls: TToolCall[] = [],
): TToolCall[] | undefined => {
  const persisted = toolCalls.filter((toolCall) => Boolean(toolCall.persisted));

  return persisted.length > 0 ? persisted : undefined;
};

export const resolveToolCalls = ({
  status,
  toolCalls,
}: TMessage): TToolCall[] | undefined => {
  if (status === 'pending') {
    return toolCalls;
  }

  const shownToolCalls =
    status === 'streaming' ? keepPersistedToolCalls(toolCalls) : toolCalls;

  return shownToolCalls?.map((toolCall) => ({ ...toolCall, status: 'done' }));
};
